
import { ReactNode, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type PatternStyle = "grid" | "dots" | "circuit" | "roadmap" | "dashboard";
export type ColorScheme = "purple" | "blue" | "teal" | "green" | "neutral";

export interface HeroBackgroundProps {
  children: ReactNode;
  pattern?: PatternStyle;
  colorScheme?: ColorScheme;
  height?: string;
  className?: string;
  overlayOpacity?: number;
  showGlass?: boolean;
  id?: string;
}

export function HeroBackground({
  children,
  pattern = "grid",
  colorScheme = "purple",
  height = "min-h-[90vh]",
  className,
  overlayOpacity = 0.15,
  showGlass = true,
  id,
}: HeroBackgroundProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [randomOffset, setRandomOffset] = useState({ x: 0, y: 0 });
  const [randomRotation, setRandomRotation] = useState(0);
  
  const colorSchemes = {
    purple: {
      gradient: "from-violet-50 via-purple-50/50 to-white",
      primary: "#6501E5",
      secondary: "#9573D0",
      accent: "#C4F63A",
      highlight: "#A4B6E2",
    },
    blue: {
      gradient: "from-blue-50 via-indigo-50/50 to-white",
      primary: "#3B82F6",
      secondary: "#93C5FD",
      accent: "#C4F63A",
      highlight: "#A4B6E2",
    },
    teal: {
      gradient: "from-teal-50 via-emerald-50/50 to-white",
      primary: "#14B8A6",
      secondary: "#5EEAD4",
      accent: "#C4F63A",
      highlight: "#A4B6E2",
    },
    green: {
      gradient: "from-green-50 via-lime-50/50 to-white",
      primary: "#22C55E",
      secondary: "#86EFAC",
      accent: "#C4F63A",
      highlight: "#A4B6E2",
    },
    neutral: {
      gradient: "from-gray-50 via-slate-50/50 to-white",
      primary: "#64748B",
      secondary: "#CBD5E1",
      accent: "#C4F63A",
      highlight: "#A4B6E2",
    },
  };

  const colors = colorSchemes[colorScheme];

  useEffect(() => {
    setRandomOffset({
      x: Math.floor(Math.random() * 50) - 25,
      y: Math.floor(Math.random() * 50) - 25
    });
    setRandomRotation(Math.floor(Math.random() * 20) - 10);
    
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const renderPatternElements = () => {
    switch (pattern) {
      case "grid":
        return (
          <div 
            className={`absolute inset-0 w-full h-full bg-repeat transition-all duration-1000 ease-out
                     ${isLoaded ? 'opacity-[0.15]' : 'opacity-0'}`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${encodeURIComponent(colors.primary)}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px',
              transform: `translate(${randomOffset.x/4}px, ${randomOffset.y/4}px) rotate(${randomRotation/4}deg)`,
              transitionDelay: '300ms'
            }}
            aria-hidden="true"
          />
        );
      case "dots":
        return (
          <div className="absolute inset-0">
            <div 
              className={`absolute inset-0 transition-opacity duration-1000 ${isLoaded ? 'opacity-20' : 'opacity-0'}`}
              style={{
                backgroundImage: `radial-gradient(${colors.primary}25 1.5px, transparent 1.5px)`,
                backgroundSize: '30px 30px',
                transitionDelay: '200ms'
              }}
            />
            <div className={`absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full transition-all duration-1500 ease-out
                         ${isLoaded ? 'opacity-20 blur-[50px]' : 'opacity-0 blur-[70px]'}`}
                 style={{
                   background: `radial-gradient(circle, ${colors.accent}80, ${colors.primary}30)`,
                   transform: `translate(${randomOffset.x}px, ${randomOffset.y}px)`,
                   transitionDelay: '500ms'
                 }} />
          </div>
        );
      case "circuit":
        return (
          <>
            <div className="absolute inset-0">
              <div className={`absolute top-[10%] left-[20%] w-[250px] h-[250px] transition-all duration-1000 ease-out
                           ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                   style={{ 
                     transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px) rotate(${randomRotation}deg)`,
                     transitionDelay: '200ms'
                   }}>
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-30">
                  <path d="M10,30 L40,30 L40,10 L70,10 L70,50 L90,50" 
                        fill="none" 
                        stroke={colors.primary} 
                        strokeWidth="1.5"
                        strokeDasharray={isLoaded ? "100 100" : "0 100"}
                        style={{ transition: "all 1.5s ease-out" }} />
                  <path d="M10,70 L30,70 L30,90 L60,90 L60,50 L90,50" 
                        fill="none" 
                        stroke={colors.secondary} 
                        strokeWidth="1.5" 
                        strokeDasharray={isLoaded ? "100 100" : "0 100"}
                        style={{ transition: "all 1.5s ease-out", transitionDelay: "300ms" }} />
                  <circle cx="10" cy="30" r="2.5" fill={colors.accent} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "600ms" }} />
                  <circle cx="10" cy="70" r="2.5" fill={colors.accent} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "800ms" }} />
                  <circle cx="40" cy="10" r="2.5" fill={colors.accent} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "700ms" }} />
                  <circle cx="70" cy="50" r="2.5" fill={colors.accent} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "900ms" }} />
                  <circle cx="90" cy="50" r="3.5" fill={colors.primary} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "1000ms" }} />
                  <circle cx="30" cy="90" r="2.5" fill={colors.accent} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "1100ms" }} />
                </svg>
              </div>
              <div className={`absolute bottom-[15%] right-[10%] w-[200px] h-[200px] transition-all duration-1000 ease-out
                           ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                   style={{ 
                     transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px) rotate(${randomRotation}deg)`,
                     transitionDelay: '400ms'
                   }}>
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-30">
                  <path d="M10,10 L90,10 L90,90 L10,90 L10,10" 
                        fill="none" 
                        stroke={colors.primary} 
                        strokeWidth="1.5" 
                        strokeDasharray="5,5"
                        style={{ transition: "all 1.5s ease-out" }} />
                  <path d="M30,30 L70,30 L70,70 L30,70 L30,30" 
                        fill="none" 
                        stroke={colors.secondary} 
                        strokeWidth="1.5"
                        style={{ transition: "all 1.5s ease-out", transitionDelay: "200ms" }} />
                  <path d="M40,40 L60,40 L60,60 L40,60 L40,40" 
                        fill={`${colors.accent}30`}
                        className={`transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                        style={{ transitionDelay: "400ms" }} />
                  <circle cx="10" cy="10" r="2.5" fill={colors.highlight} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "600ms" }} />
                  <circle cx="90" cy="10" r="2.5" fill={colors.highlight} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "700ms" }} />
                  <circle cx="90" cy="90" r="2.5" fill={colors.highlight} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "800ms" }} />
                  <circle cx="10" cy="90" r="2.5" fill={colors.highlight} className={`transition-all duration-500 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: "900ms" }} />
                </svg>
              </div>
            </div>
          </>
        );
      case "roadmap":
        return (
          <>
            <div className="absolute inset-0 overflow-hidden">
              <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 800 600" preserveAspectRatio="none">
                <path 
                  d={`M0,${300 + randomOffset.y} C${200 + randomOffset.x},${100 - randomOffset.y} ${400 - randomOffset.x},${500 + randomOffset.y} 800,${200 - randomOffset.y}`} 
                  fill="none" 
                  stroke={colors.primary} 
                  strokeWidth="2"
                  strokeDasharray={isLoaded ? "1000 1000" : "0 1000"}
                  className="transition-all duration-2000 ease-out"
                  style={{ transitionDelay: '300ms' }}
                />
                
                <path 
                  d={`M0,${350 - randomOffset.y} C${300 - randomOffset.x},${200 + randomOffset.y} ${500 + randomOffset.x},${400 - randomOffset.y} 800,${300 + randomOffset.y}`} 
                  fill="none" 
                  stroke={colors.secondary} 
                  strokeWidth="1.5"
                  strokeDasharray={isLoaded ? "1000 1000" : "0 1000"}
                  className="transition-all duration-2000 ease-out"
                  style={{ transitionDelay: '500ms' }}
                />
                
                <path 
                  d={`M0,${400 + randomOffset.y/2} C${250 - randomOffset.x/2},${350 - randomOffset.y/2} ${550 + randomOffset.x/2},${250 + randomOffset.y/2} 800,${450 - randomOffset.y/2}`} 
                  fill="none" 
                  stroke={colors.accent} 
                  strokeWidth="1"
                  strokeDasharray={isLoaded ? "1000 1000" : "0 1000"}
                  className="transition-all duration-2000 ease-out"
                  style={{ transitionDelay: '700ms' }}
                />
              </svg>

              <div className={`absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full opacity-10 transition-all duration-1500 ease-out
                           ${isLoaded ? 'opacity-10 blur-3xl' : 'opacity-0 blur-xl'}`}
                   style={{ 
                     background: `radial-gradient(circle, ${colors.accent}50, ${colors.primary}30)`,
                     transform: `translate(${randomOffset.x}px, ${randomOffset.y}px)`,
                     transitionDelay: '300ms'
                   }} />
                   
              <div className={`absolute top-2/3 right-1/3 w-48 h-48 transition-all duration-1000 ease-out
                           ${isLoaded ? 'opacity-10' : 'opacity-0'}`}
                   style={{ 
                     transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px) rotate(${45 + randomRotation}deg)`,
                     transitionDelay: '500ms',
                     background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}10)`
                   }} />
                   
              <div className={`absolute top-1/3 right-1/4 w-72 h-72 rounded-full transition-all duration-1500 ease-out
                           ${isLoaded ? 'opacity-10' : 'opacity-0'}`}
                   style={{ 
                     background: `radial-gradient(circle, ${colors.highlight}20, ${colors.secondary}10)`,
                     transform: `translate(${-randomOffset.x}px, ${-randomOffset.y}px)`,
                     transitionDelay: '200ms'
                   }} />
            </div>
          </>
        );
      case "dashboard":
        return (
          <>
            <div className={`absolute top-[15%] left-[20%] w-[250px] h-[250px] transition-all duration-1000 ease-out
                         ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                 style={{ 
                   transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px) rotate(${randomRotation}deg)`,
                   transitionDelay: '200ms'
                 }}>
              <div className="absolute inset-3 rounded-full border-8 border-opacity-30"
                   style={{ borderColor: `${colors.primary}` }} />
              <div className="text-5xl font-bold opacity-30" style={{ color: `${colors.primary}` }}>145</div>
            </div>
            
            <div className={`absolute top-1/4 right-[15%] w-[300px] h-[250px] grid grid-cols-3 grid-rows-4 gap-2 transform transition-all duration-1000 ease-out
                          ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                 style={{ 
                   transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px) rotate(${6 + randomRotation/2}deg)`,
                   transitionDelay: '400ms'
                 }}>
              {Array(12).fill(0).map((_, i) => (
                <div key={i} className={`rounded-md transition-all duration-700 shadow-md ${isLoaded ? 'opacity-100' : 'opacity-0'}`} style={{
                  backgroundColor: `${
                    i % 4 === 0 ? `${colors.primary}25` : 
                    i % 3 === 0 ? `${colors.accent}25` : 
                    i % 2 === 0 ? `${colors.secondary}25` : `${colors.highlight}25`
                  }`,
                  transitionDelay: `${100 + i * 50}ms`
                }} />
              ))}
            </div>
            
            <div className={`absolute bottom-[20%] left-[15%] w-[400px] h-[100px] flex items-end transition-all duration-1000 ease-out
                          ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                 style={{ 
                   transform: `translate(${randomOffset.x/3}px, ${randomOffset.y/3}px)`,
                   transitionDelay: '200ms'
                 }}>
              {Array(12).fill(0).map((_, i) => (
                <div 
                  key={i} 
                  className={`w-[30px] mx-[2px] rounded-t-sm transition-all duration-500 shadow-lg ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    height: `${20 + Math.random() * 80}px`,
                    background: `linear-gradient(to top, ${
                      i % 3 === 0 
                        ? `${colors.accent}40, ${colors.accent}10` 
                        : `${colors.primary}30, ${colors.primary}10`
                    })`,
                    transitionDelay: `${i * 70}ms`
                  }} 
                />
              ))}
            </div>
            
            <div className={`absolute bottom-[15%] right-[20%] flex space-x-6 transition-all duration-1000 ease-out
                          ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                 style={{ 
                   transform: `translate(${randomOffset.x/2}px, ${randomOffset.y/2}px)`,
                   transitionDelay: '500ms'
                 }}>
              {[65, 78, 92].map((value, i) => (
                <div key={i} className={`relative w-[80px] h-[80px] rounded-full border-4 flex items-center justify-center shadow-lg transition-all duration-700
                                      ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                     style={{ 
                       borderColor: `${colors.secondary}20`,
                       transitionDelay: `${600 + i * 150}ms`
                     }}>
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                    <circle className="opacity-20" strokeWidth="8" stroke="currentColor" fill="transparent" r="40" cx="50" cy="50" />
                    <circle 
                      style={{ 
                        color: i === 0 ? `${colors.primary}30` : 
                               i === 1 ? `${colors.accent}30` : 
                               `${colors.highlight}30`,
                        transition: "all 1s ease-out",
                        strokeDasharray: isLoaded ? `${value * 2.51} 251.2` : "0 251.2",
                        transitionDelay: `${700 + i * 150}ms`
                      }}
                      strokeWidth="8" 
                      strokeLinecap="round" 
                      stroke="currentColor" 
                      fill="transparent" 
                      r="40" 
                      cx="50" 
                      cy="50" 
                    />
                  </svg>
                  <div className="text-xs font-semibold opacity-40" style={{ color: colors.primary }}>{value}%</div>
                </div>
              ))}
            </div>
          </>
        );
      default:
        return (
          <div 
            className={`absolute inset-0 w-full h-full bg-repeat transition-all duration-1000 ease-out
                     ${isLoaded ? 'opacity-[0.15]' : 'opacity-0'}`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${encodeURIComponent(colors.primary)}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}
            aria-hidden="true"
          />
        );
    }
  };

  const renderRandomDecorations = () => (
    <>
      {isLoaded && (
        <>
          <div className="absolute top-[15%] right-[30%] w-[300px] h-[300px] rounded-full opacity-10 animate-blob animation-delay-2000"
               style={{
                 background: `radial-gradient(circle, ${colors.primary}90, ${colors.secondary}40)`,
                 filter: 'blur(40px)',
                 transform: `translate(${randomOffset.x}px, ${randomOffset.y}px)`
               }} />
          
          <div className="absolute bottom-[20%] left-[20%] w-[250px] h-[250px] rounded-full opacity-10 animate-blob animation-delay-4000"
               style={{
                 background: `radial-gradient(circle, ${colors.accent}60, ${colors.highlight}30)`,
                 filter: 'blur(30px)',
                 transform: `translate(${randomOffset.x * -1}px, ${randomOffset.y * -1}px)`
               }} />
               
          <div className={`absolute top-[40%] right-[10%] w-[100px] h-[100px] transition-all duration-1000
                        ${isLoaded ? 'opacity-20 rotate-45' : 'opacity-0 rotate-0'}`}
               style={{
                 borderLeft: `3px solid ${colors.primary}`,
                 borderBottom: `3px solid ${colors.accent}`,
                 transitionDelay: '800ms'
               }} />
        </>
      )}
    </>
  );

  return (
    <section className={cn(`relative flex items-start md:items-center overflow-hidden pt-28 md:pt-32`, height, className)} aria-labelledby="hero-heading" id={id}>
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />
      
      <div className={`absolute inset-0 transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
           style={{
             background: `linear-gradient(135deg, 
                          ${colors.primary}05 0%, 
                          ${colors.primary}10 25%, 
                          ${colors.primary}05 50%,
                          ${colors.primary}10 75%,
                          ${colors.primary}05 100%)`,
             transitionDelay: '300ms'
           }} />
      
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br" 
             style={{ 
               backgroundImage: `linear-gradient(to bottom right, ${colors.primary}05, ${colors.secondary}05)` 
             }} />
        
        <div className="absolute inset-0 overflow-hidden">
          {renderPatternElements()}
          {renderRandomDecorations()}
        </div>
      </div>
      
      {showGlass && (
        <div className={`absolute inset-0 z-1`}>
          <div className="absolute inset-0 backdrop-blur-[2px] bg-white/30" />
          
          <div className={`absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-white/40 to-transparent backdrop-blur-md transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1500 ease-out ${isLoaded ? 'opacity-40' : 'opacity-0'}`} />
          <div className={`absolute bottom-1/3 right-1/4 w-[300px] h-[300px] rounded-full backdrop-blur-sm transition-all duration-1500 ease-out ${isLoaded ? 'opacity-30' : 'opacity-0'}`}
               style={{ 
                 backgroundImage: `linear-gradient(to top left, ${colors.primary}15, transparent)`,
               }} />
          
          <div className={`absolute top-[10%] right-[30%] w-[100px] h-[200px] bg-white/15 rounded-full blur-xl transform rotate-45 transition-all duration-1000 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />
          <div className={`absolute bottom-[20%] left-[25%] w-[150px] h-[80px] bg-white/10 rounded-full blur-xl transform -rotate-12 transition-all duration-1000 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />
        </div>
      )}
      
      <div className="relative z-10 w-full">
        {children}
      </div>
    </section>
  );
}
